import { useMemo } from 'react';
import useSlingApi from '@sdk/api/useSlingApi';
import { extendBillingInfo } from '@sdk/account/utils';
import gettext from '@sdk/lang/gettext';
import { SUBSCRIPTION_BILLING_CYCLES, SUBSCRIPTION_LEVELS } from '@sdk/account/constants';
import useToast from '@sdk/common/toast/useToast';
import type { CurrencyType } from '@sdk/common/currency/types/CurrencyType';
import type { OrgSettings } from '@sdk/authentication/types/OrganisationType';
import getErrorForToast from '@sdk/api/utils/getErrorForToast';
import addYears from '@sdk/utils/addYears';
import usePromiseModalState from '@sdk/components/ui/modal/usePromiseModalState';
import { PurchaseSubscriptionPayload } from '@sdk/account/types/apiTypes';

import { YEAR_MONTHS, getIsYearlyPlan, getYearlyDiscountPercentage } from '@web/account/utils';
import getSubscriptionCycleOptions from '@web/account/utils/getSubscriptionCycleOptions';
import { subscriptionLevelsPickerOptions } from '@web/account/constants';
import { useBillingInfo } from '@web/account/billing/BillingInfoContext';
import type { SubscriptionUtilsType } from '@web/account/types';
import { useStripeUtils } from '@web/account/hooks/stripeUtils.hooks';

export const useUpgradeSubscriptionModal = ({
  subscriptionUtils,
  level,
  currency,
  subscriptionBillingCycle,
  orgSettings,
  currentBillingCycle,
  setUpgradeCost,
  onSuccessSubmitCallback,
}: {
  subscriptionUtils: SubscriptionUtilsType;
  level: number;
  currency: CurrencyType;
  subscriptionBillingCycle: SUBSCRIPTION_BILLING_CYCLES;
  orgSettings: OrgSettings;
  currentBillingCycle: string;
  setUpgradeCost: (cost: number) => void;
  onSuccessSubmitCallback?: () => void;
}) => {
  const api = useSlingApi();
  const { showErrorToast, showSuccessToast } = useToast();
  const creditCardModalState = usePromiseModalState<string>();
  const { billingInfo, onBillingInfoUpdated } = useBillingInfo();
  const { authenticateCard } = useStripeUtils();
  const currentOrFormerLevel = billingInfo.subscriptionLevel || billingInfo.formerSubscriptionLevel;
  const currentOrFormerIsYearlyPlan = billingInfo.subscriptionLevel
    ? billingInfo.isYearlyPlan
    : billingInfo.formerIsYearlyPlan;

  const areYearlySubscriptionsEnabled =
    subscriptionUtils.areYearlySubscriptionsEnabled(billingInfo);

  const monthlyPricePerUserPerMonth = useMemo(
    () => subscriptionUtils.getPricePerUnit(level, false),
    [subscriptionUtils, level]
  );

  const yearlyPricePerUserPerMonth = useMemo(
    () => subscriptionUtils.getPricePerUnit(level, true) / YEAR_MONTHS,
    [subscriptionUtils, level]
  );

  const yearlyPlanDiscount = getYearlyDiscountPercentage(level, orgSettings);

  const subscriptionCycles = getSubscriptionCycleOptions({
    currency,
    monthlyPricePerUserPerMonth,
    yearlyPricePerUserPerMonth,
    yearlyPlanDiscount,
  });

  const subscriptionLevelsWithCurrrent = useMemo(() => {
    return subscriptionLevelsPickerOptions.map((opt) => {
      return {
        ...opt,
        label:
          opt.value === currentOrFormerLevel
            ? `${opt.label} ${gettext('(current plan)')}`
            : opt.label,
      };
    });
  }, [currentOrFormerLevel]);

  const isSubscriptionLevelChanged = level !== currentOrFormerLevel;
  const isSubscriptionCycleChanged = subscriptionBillingCycle !== currentBillingCycle;
  const isAnythingChanged = isSubscriptionLevelChanged || isSubscriptionCycleChanged;
  const isYearlyPlan = getIsYearlyPlan(subscriptionBillingCycle);

  const showPayingToday = Boolean(
    isAnythingChanged && isYearlyPlan && billingInfo.subscriptionLevel
  );

  const showCyclePicker = !isYearlyPlan;
  const showPlanPicker = currentOrFormerLevel < SUBSCRIPTION_LEVELS.BUSINESS && showCyclePicker;

  const updateOrRepurchase = async (payload: {
    subscriptionLevel?: number;
    isYearlyPlan?: boolean;
    supportSca: boolean;
  }) => {
    if (billingInfo.subscriptionStartDate) {
      const { data } = await api.account.updateSubscription(payload);
      await authenticateCard(data);
      return data;
    }

    const newPaymentMethodId = await creditCardModalState.getResult();
    const purchasePayload: PurchaseSubscriptionPayload = {
      ...payload,
      subscriptionLevel: payload.subscriptionLevel || level,
    };

    if (newPaymentMethodId) {
      purchasePayload.newPaymentMethodId = newPaymentMethodId;
    }

    const { data } = await api.account.purchaseSubscription(purchasePayload);

    await authenticateCard(data);
    showSuccessToast(gettext('Your subscription has been reactivated'));
    return data;
  };

  const onSubmit = async () => {
    const payload = {
      subscriptionLevel: isSubscriptionLevelChanged ? level : undefined,
      isYearlyPlan:
        isSubscriptionCycleChanged || !billingInfo.subscriptionLevel ? isYearlyPlan : undefined,
      supportSca: true,
    };

    try {
      const data = await updateOrRepurchase(payload);
      onBillingInfoUpdated(extendBillingInfo(data));
      onSuccessSubmitCallback?.();
    } catch (e) {
      if (!billingInfo.subscriptionLevel) {
        showErrorToast(gettext("Your payment attempt didn't work"));
      } else {
        showErrorToast(getErrorForToast(e));
      }

      throw e; // for the ProgressButton
    }
  };

  const getUpgradeCost = async (level: number) => {
    setUpgradeCost(null);
    try {
      const { data: cost } = await api.account.getUpgradeCost({ level, isYearlyPlan: true });
      setUpgradeCost(cost);
    } catch (e) {
      showErrorToast(getErrorForToast(e, gettext('Failed to retrieve upgrade cost')));
    }
  };

  let overriddenNextChargeDate: Date;
  if (isYearlyPlan && !currentOrFormerIsYearlyPlan) {
    overriddenNextChargeDate = addYears(new Date(), 1);
  }

  return {
    areYearlySubscriptionsEnabled,
    subscriptionCycles,
    subscriptionLevelsWithCurrrent,
    showPayingToday,
    isAnythingChanged,
    onSubmit,
    showPlanPicker,
    showCyclePicker,
    getUpgradeCost,
    overriddenNextChargeDate,
    creditCardModalState,
  };
};
